import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { User } from '../classes/user.class';
import { LocalStorage } from '../storage.class';
import { ApiResponse, ApiService } from './api/api.service';
import { WebsocketService } from './websocket/websocket.service';

// Interface for AiReport model
export interface AiReport {
    id: number;
    title: string;
    created_by: number;
    validated: boolean;
    query: string;
    latest_update: Date;
    user_id: number;
    shared: boolean;
    created_at: Date;
    updated_at: Date;

    // Relationships
    creator?: User;
    user?: User;
}

// Interface for paginated results
export interface PaginatedResults {
    results: any[];
    total_count: number;
    shown_count: number;
    hidden_count: number;
    has_more: boolean;
}

// Interface for AiReportsMessage model
export interface AiReportsMessage {
    id: number;
    ai_report_id: number;
    ai: boolean;
    codalt: boolean;
    message: string;
    limit: number;
    date_start: Date;
    date_end: Date;
    query: string;
    results?: any[] | null;
    paginatedResults?: PaginatedResults; // Add pagination info
    currentLimit?: number; // Track current limit for this message
    user_id: number;
    created_at: Date;
    updated_at: Date;
    expanded?: boolean;
    has_results?: boolean; // Flag to indicate results are available via HTTP
    loadingExcel: boolean;
}

export class AiReportsMessageData {
    data: AiReportsMessage[];
    count: number;
}

@Injectable({ providedIn: 'root' })
export class AiReportsService {
    public totalCount = 0;
    private items: AiReportsMessage[] = [];
    private itemChanged = new EventEmitter<number[]>();
    private messageResultsNeeded = new EventEmitter<AiReportsMessage>();
    private currentAiReportId: number;
    private retryTimeoutPointer;
    private init = false;

    constructor(
        private apiService: ApiService,
        private websocket: WebsocketService
    ) {
        this.websocketInit();
    }

    public getList(chatId: number): Observable<AiReportsMessage[]> {
        if (!this.init) {
            this.websocketInit();
        }
        return new Observable<AiReportsMessage[]>(observer => {
            const itemChangedSubscription = this.itemChanged.subscribe(
                () => {
                    observer.next(this.items);
                },
                () => {
                    observer.error();
                }
            );

            if (chatId !== this.currentAiReportId) {
                this.currentAiReportId = chatId;
                this.items = [];
            } else if (this.items?.length) {
                observer.next(this.items);
            }
            this.requestMessages(chatId);

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                },
            };
        });
    }

    public onMessageResultsNeeded(): Observable<AiReportsMessage> {
        return this.messageResultsNeeded.asObservable();
    }

    public requestMessages(chatId: number, fromId: string | number = '', limit = 25) {
        this.websocket.doRequest('aiReportMessages', null, null, chatId);
    }

    private websocketInit() {
        this.init = true;
        this.websocket.getWebsocket<AiReportsMessageData>('aiReportMessages').subscribe({
            next: wsItems => {
                if (wsItems?.success) {
                    this.websocketHandling(wsItems.data.data);
                } else if (!!wsItems?.reinit) {
                    this.retryWebsocketInit();
                }
            },
            error: () => this.retryWebsocketInit(),
            complete: () => this.retryWebsocketInit(),
        });
    }

    private websocketHandling(wsItems: AiReportsMessage[]) {
        const currentUser = LocalStorage.getUser();

        if (typeof wsItems !== 'undefined') {
            // Filter messages to only include those from the current user
            const userMessages = wsItems.filter(
                m => m.ai_report_id === +this.currentAiReportId && (!m.user_id || m.user_id === currentUser?.id)
            );

            this.totalCount = userMessages.length;
        }

        if (this.items && wsItems) {
            // Filter to only process messages for current report and current user
            wsItems
                .filter(
                    i => +i.ai_report_id === +this.currentAiReportId && (!i.user_id || i.user_id === currentUser?.id)
                )
                .forEach(item => {
                    // Always remove results from websocket data to force HTTP loading
                    const cleanItem = { ...item };
                    delete cleanItem.results;

                    const currentItem = this.items.find(p => p.id === +item.id);
                    if (currentItem) {
                        Object.assign(currentItem, cleanItem);
                        // Only emit if this message has a query and doesn't already have results
                        if (cleanItem.query && cleanItem.query.trim() !== '' && !currentItem.results) {
                            this.messageResultsNeeded.emit(currentItem);
                        }
                    } else {
                        this.items.push(cleanItem);
                        // Only emit if this new message has a query and doesn't already have results
                        if (cleanItem.query && cleanItem.query.trim() !== '' && !cleanItem.results) {
                            this.messageResultsNeeded.emit(cleanItem);
                        }
                    }
                });

            this.items.sort((a, b) => {
                const aa = new Date(a.created_at);
                const bb = new Date(b.created_at);
                if (aa.getTime() < bb.getTime()) {
                    return -1;
                }
                if (aa.getTime() > bb.getTime()) {
                    return 1;
                }
                return 0;
            });

            this.itemChanged.emit(wsItems.map(i => +i.id));
        }
    }

    private retryWebsocketInit() {
        this.init = false;
        if (this.retryTimeoutPointer) {
            clearTimeout(this.retryTimeoutPointer);
        }
        this.retryTimeoutPointer = setTimeout(() => {
            this.websocketInit();
        }, 1000);
    }

    /**
     * Get all AI Reports
     * @returns Observable<ApiResponse<AiReport[]>>
     */
    getAiReports(): Observable<ApiResponse<AiReport[]>> {
        return this.apiService.getCallObs<ApiResponse<AiReport[]>>('ai/reports');
    }

    /**
     * Get AI Reports with optional filters
     * @param filters - Optional filters for the query
     * @returns Observable<ApiResponse<AiReport[]>>
     */
    getAiReportsWithFilters(
        filters: {
            validated?: boolean;
            shared?: boolean;
            created_by?: number;
            user_id?: number;
        } = {}
    ): Observable<ApiResponse<AiReport[]>> {
        return this.apiService.getCallObs<ApiResponse<AiReport[]>>('ai/reports');
    }

    /**
     * Get a specific AI Report by ID
     * @param id - The AI Report ID
     * @returns Observable<ApiResponse<AiReport>>
     */
    getAiReport(id: number): Observable<ApiResponse<AiReport>> {
        return this.apiService.getCallObs<ApiResponse<AiReport>>(`ai-reports/${id}`);
    }

    /**
     * Get messages for a specific AI Report
     * @param reportId - The AI Report ID
     * @returns Observable<ApiResponse<AiReportsMessage[]>>
     */
    getAiReportMessages(reportId: number): Observable<ApiResponse<AiReportsMessage[]>> {
        return this.apiService.getCallObs<ApiResponse<AiReportsMessage[]>>(`ai/reports/messages?singleId=${reportId}`);
    }

    /**
     * Send a message to AI
     * @param reportId - The AI Report ID
     * @param message - The message to send
     * @param options - Optional parameters
     * @returns Observable<ApiResponse<AiReportsMessage>>
     */
    sendMessage(
        reportId: number,
        message: string,
        options: {
            limit?: number;
            date_start?: string;
            date_end?: string;
        } = {}
    ): Observable<ApiResponse<AiReportsMessage>> {
        const payload = {
            ai_report_id: reportId,
            message: message,
            limit: options.limit || 100,
            date_start: options.date_start,
            date_end: options.date_end,
            ai: false,
            codalt: false,
        };

        return this.apiService.postCallObserver<ApiResponse<AiReportsMessage>>('ai/send-message', payload);
    }

    /**
     * Get the results for a specific message
     * @param messageId - The message ID
     * @param limit - Maximum number of records to return (default: 20)
     * @returns Observable<ApiResponse<any>>
     */
    getMessageResults(messageId: number, limit: number = 20): Observable<ApiResponse<PaginatedResults>> {
        return this.apiService.getCallObs<ApiResponse<PaginatedResults>>(
            `ai/messages/results/${messageId}?limit=${limit}`
        );
    }

    /**
     * Delete an AI Report
     * @param reportId - The AI Report ID to delete
     * @returns Observable<ApiResponse<any>>
     */
    deleteAiReport(reportId: number): Observable<ApiResponse<any>> {
        return this.apiService.deleteCall$<ApiResponse<any>>(`ai/reports/${reportId}`);
    }

    /**
     * Clear all messages in an AI Report for the current user
     * @param reportId - The AI Report ID to clear messages from
     * @returns Observable<ApiResponse<any>>
     */
    clearChat(reportId: number): Observable<ApiResponse<any>> {
        return this.apiService.postCallObserver<ApiResponse<any>>(`ai/reports/clear`, { reportId });
    }
}
